import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Clock, PackageX, Users, AlertTriangle, GitCompareArrows } from 'lucide-react';
import { formatNumber, formatMaterialMap } from '../../lib/utils';

// Card config: which alert each card represents
const ALERT_CARDS = [
  { key: 'delayed_lrs', title: 'Delayed LR Alert', subtitle: 'In Transport > 15 days', icon: Clock, accent: 'red' },
  { key: 'issued_not_received', title: 'Issued, Not Received', subtitle: 'Issued > 3 days, no receipt', icon: PackageX, accent: 'amber' },
  { key: 'top_pending_suppliers', title: 'Top Pending Suppliers', subtitle: 'Highest pending quantity', icon: Users, accent: 'blue' },
  { key: 'wrong_data_alerts', title: 'Wrong Data Alert', subtitle: 'Data integrity issues', icon: AlertTriangle, accent: 'purple' },
  { key: 'receipt_mismatches', title: 'Receipt Mismatch', subtitle: 'LR qty ≠ Receipt qty', icon: GitCompareArrows, accent: 'orange' },
];

const ACCENT = {
  red: 'border-red-200 bg-red-50/60 hover:bg-red-100 text-red-700',
  amber: 'border-amber-200 bg-amber-50/60 hover:bg-amber-100 text-amber-700',
  blue: 'border-blue-200 bg-blue-50/60 hover:bg-blue-100 text-blue-700',
  purple: 'border-purple-200 bg-purple-50/60 hover:bg-purple-100 text-purple-700',
  orange: 'border-orange-200 bg-orange-50/60 hover:bg-orange-100 text-orange-700',
};

const Th = ({ children, right }) => (
  <th className={`py-2 px-3 font-semibold text-slate-600 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, right, mono }) => (
  <td className={`py-2 px-3 text-slate-700 ${right ? 'text-right' : ''} ${mono ? 'font-mono' : ''}`}>{children}</td>
);

const renderTable = (key, rows) => {
  if (!rows || rows.length === 0) {
    return <div className="text-center py-10 text-slate-400">No issues found 🎉</div>;
  }
  switch (key) {
    case 'delayed_lrs':
      return (
        <table className="w-full text-sm"><thead><tr className="border-b border-slate-200 bg-slate-50">
          <Th>LR Number</Th><Th>Supplier</Th><Th>Quality</Th><Th>Transport</Th><Th right>Material</Th><Th>LR Date</Th><Th right>Delay</Th>
        </tr></thead><tbody>
          {rows.map((r) => (
            <tr key={r.lr_id} data-testid={`delayed-lr-row-${r.lr_number}`} className="border-b border-slate-100">
              <Td mono>{r.lr_number}</Td><Td>{r.supplier || '-'}</Td><Td>{r.quality || '-'}</Td><Td>{r.transport || '-'}</Td>
              <Td right>{r.material_quantity != null ? `${formatNumber(r.material_quantity)} ${r.unit}` : `${formatNumber(r.bales)} Bales`}</Td>
              <Td>{r.lr_date}</Td><Td right><span className="text-red-600 font-semibold">{r.delay_days}d</span></Td>
            </tr>
          ))}
        </tbody></table>
      );
    case 'issued_not_received':
      return (
        <table className="w-full text-sm"><thead><tr className="border-b border-slate-200 bg-slate-50">
          <Th>LR Number</Th><Th>Supplier</Th><Th>Quality</Th><Th right>Material</Th><Th>Issue Date</Th><Th right>Pending</Th>
        </tr></thead><tbody>
          {rows.map((r) => (
            <tr key={r.lr_id} data-testid={`issued-not-received-row-${r.lr_number}`} className="border-b border-slate-100">
              <Td mono>{r.lr_number}</Td><Td>{r.supplier || '-'}</Td><Td>{r.quality || '-'}</Td>
              <Td right>{r.material_quantity != null ? `${formatNumber(r.material_quantity)} ${r.unit}` : `${formatNumber(r.bales)} Bales`}</Td>
              <Td>{r.issue_date}</Td><Td right><span className="text-amber-600 font-semibold">{r.pending_days}d</span></Td>
            </tr>
          ))}
        </tbody></table>
      );
    case 'top_pending_suppliers':
      return (
        <table className="w-full text-sm"><thead><tr className="border-b border-slate-200 bg-slate-50">
          <Th>Supplier</Th><Th right>Pending Quantity</Th><Th right>Pending Orders</Th>
        </tr></thead><tbody>
          {rows.map((r) => (
            <tr key={r.supplier} data-testid={`pending-supplier-row-${r.supplier}`} className="border-b border-slate-100">
              <Td>{r.supplier}</Td><Td right>{formatMaterialMap(r.pending_by_unit)}</Td><Td right>{r.pending_orders}</Td>
            </tr>
          ))}
        </tbody></table>
      );
    case 'wrong_data_alerts':
      return (
        <table className="w-full text-sm"><thead><tr className="border-b border-slate-200 bg-slate-50">
          <Th>Type</Th><Th>Reference</Th><Th>Detail</Th>
        </tr></thead><tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`wrong-data-row-${i}`} className="border-b border-slate-100">
              <Td><Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">{r.type}</Badge></Td>
              <Td mono>{r.reference}</Td><Td>{r.detail}</Td>
            </tr>
          ))}
        </tbody></table>
      );
    case 'receipt_mismatches':
      return (
        <table className="w-full text-sm"><thead><tr className="border-b border-slate-200 bg-slate-50">
          <Th>LR Number</Th><Th>Quality</Th><Th right>LR Qty</Th><Th right>Receipt Qty</Th><Th right>Difference</Th>
        </tr></thead><tbody>
          {rows.map((r) => (
            <tr key={r.lr_id} data-testid={`receipt-mismatch-row-${r.lr_number}`} className="border-b border-slate-100">
              <Td mono>{r.lr_number}</Td><Td>{r.quality || '-'}</Td>
              <Td right>{formatNumber(r.lr_quantity)} {r.unit}</Td>
              <Td right>{formatNumber(r.receipt_quantity)} {r.unit}</Td>
              <Td right><span className={r.difference < 0 ? 'text-red-600 font-semibold' : 'text-amber-600 font-semibold'}>{r.difference > 0 ? '+' : ''}{formatNumber(r.difference)}</span></Td>
            </tr>
          ))}
        </tbody></table>
      );
    default:
      return null;
  }
};

/**
 * Per-item Smart Alerts. `alerts` is the item's `smart_alerts` object from the itemwise endpoint
 * and `itemName` scopes the data-testids. Renders 5 compact cards inside the item section.
 */
const SmartAlerts = ({ alerts, itemName }) => {
  const [modal, setModal] = useState({ open: false, key: null });
  const counts = alerts?.counts || {};
  const active = modal.key ? ALERT_CARDS.find(c => c.key === modal.key) : null;

  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" /> Smart Alerts
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid={`smart-alerts-grid-${itemName}`}>
        {ALERT_CARDS.map((c) => {
          const Icon = c.icon;
          const count = counts[c.key] || 0;
          return (
            <Card key={c.key} data-testid={`smart-alert-${c.key}-${itemName}`}
              className={`border shadow-sm cursor-pointer transition-colors ${ACCENT[c.accent]}`}
              onClick={() => setModal({ open: true, key: c.key })}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <Icon className="h-4 w-4" />
                  <span data-testid={`smart-alert-count-${c.key}-${itemName}`} className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    {count}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-800 leading-tight">{c.title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{c.subtitle}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ open: o, key: o ? modal.key : null })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="smart-alert-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {active && <active.icon className="h-5 w-5" />} {itemName} — {active?.title}
            </DialogTitle>
            <DialogDescription>{active?.subtitle}</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            {modal.key && renderTable(modal.key, alerts?.[modal.key])}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SmartAlerts;
